import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PauseCircle } from 'lucide-react';
import { Card } from './ui/Card';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogTrigger } from './ui/Dialog';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Alert } from './ui/Alert';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  STEP_UP_ENDED_MESSAGE,
  isStepUpRequired,
  stepUpBody,
  stepUpCodeReady,
  useStepUpWindow,
} from '../lib/stepUp';
import { StepUpCodeField } from './StepUpCodeField';
import { ApiError, type SenderPaused } from '../types/api';

const KIND_TITLE: Record<NonNullable<SenderPaused['kind']>, string> = {
  ses_halt: 'Stopped by SES',
  auto_pause: 'Stopped by the bounce or complaint rate',
  other: 'Stopped',
};

const RESUME_ACKNOWLEDGEMENT = 'RESUME';

function resumeErrorText(err: unknown): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (isStepUpRequired(err)) return STEP_UP_ENDED_MESSAGE;
  if (err instanceof ApiError && err.status === 401)
    return 'The code is incorrect or already used.';
  if (err instanceof ApiError && err.status === 409 && err.body.message) return err.body.message;
  return 'The sender could not be resumed. Try again.';
}

function ResumeSenderDialog({
  senderId,
  kind,
  killSwitchEngaged,
}: {
  senderId: string;
  kind: 'ses_halt' | 'auto_pause';
  killSwitchEngaged: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [acknowledge, setAcknowledge] = useState('');
  const queryClient = useQueryClient();
  const stepUp = useStepUpWindow();
  // HealthRail renders its body twice (mobile <details> + desktop aside),
  // so ids must be unique per instance.
  const codeId = useId();
  const ackId = useId();
  const ackHintId = useId();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch(`/senders/${encodeURIComponent(senderId)}/resume`, {
        method: 'POST',
        body: {
          ...(stepUp.active ? {} : stepUpBody(totpCode)),
          ...(kind === 'auto_pause' ? { acknowledge: acknowledge.trim() } : {}),
        },
      }),
    onSuccess: () => {
      stepUp.onStepUpSuccess();
      setOpen(false);
      setTotpCode('');
      setAcknowledge('');
      void queryClient.invalidateQueries({ queryKey: ['health-summary'] });
    },
    onError: stepUp.onStepUpError,
  });

  const ackOk =
    kind !== 'auto_pause' || acknowledge.trim().toUpperCase() === RESUME_ACKNOWLEDGEMENT;
  const canConfirm = (stepUp.active || stepUpCodeReady(totpCode)) && ackOk && !mutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setTotpCode('');
          setAcknowledge('');
          mutation.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="secondary" className="w-full h-11">
          Resume sender
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Resume sender"
        description="The sender will send again on the next run. The kill switch and the bounce/complaint limits still apply."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canConfirm) mutation.mutate();
          }}
        >
          {mutation.isError ? <Alert>{resumeErrorText(mutation.error)}</Alert> : null}
          {killSwitchEngaged ? (
            <p className="m-0 text-sm text-warning-text">
              The kill switch is engaged. Nothing will send until it is turned off.
            </p>
          ) : null}
          {kind === 'auto_pause' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ackId}>Type &quot;{RESUME_ACKNOWLEDGEMENT}&quot; to confirm</Label>
              <Input
                id={ackId}
                autoComplete="off"
                placeholder={RESUME_ACKNOWLEDGEMENT}
                value={acknowledge}
                onChange={(e) => setAcknowledge(e.target.value)}
                aria-describedby={ackHintId}
                required
              />
              <div id={ackHintId} className="text-xs text-muted">
                Confirms that the bounce or complaint rate has been investigated and accepted.
              </div>
            </div>
          ) : null}
          <StepUpCodeField
            id={codeId}
            value={totpCode}
            onChange={setTotpCode}
            active={stepUp.active}
            minutesLeft={stepUp.minutesLeft}
            hint={
              kind === 'ses_halt'
                ? 'Check in the SES console that the cause has been resolved before resuming.'
                : 'Resuming does not automatically resume any campaigns in progress — those must be resumed individually.'
            }
          />
          <DialogFooter>
            <DialogClose>
              <Button type="button" variant="secondary" className="h-11">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" variant="primary" disabled={!canConfirm} className="h-11">
              {mutation.isPending ? 'Resuming…' : 'Resume sender'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Shown in the health rail while the primary sender is paused: why
 * (error code/status only, never the SES message) and since when. An
 * admin can resume an SES halt here (fresh TOTP code, audit logged);
 * a bounce/complaint auto-pause is only described.
 */
export function SenderStatusCard({
  senderPaused,
  killSwitchEngaged,
}: {
  senderPaused: SenderPaused;
  killSwitchEngaged: boolean;
}) {
  const { isAdmin } = useAuth();
  if (!senderPaused.paused) return null;

  const kind = senderPaused.kind ?? 'other';
  const canResume =
    (kind === 'ses_halt' || kind === 'auto_pause') && senderPaused.senderId !== null;

  return (
    <Card className="p-4 flex flex-col gap-2 text-sm border-danger-border">
      <div className="flex items-center gap-2">
        <PauseCircle size={16} aria-hidden="true" className="text-danger-text" />
        <h2 className="text-sm font-semibold m-0">Sender stopped</h2>
      </div>
      <div className="text-danger-text">{KIND_TITLE[kind]}</div>
      {kind === 'ses_halt' ? (
        <div className="text-xs">
          {senderPaused.errorCode ? (
            <>
              Error: <span className="font-mono">{senderPaused.errorCode}</span>
              {senderPaused.httpStatus !== null ? (
                <>
                  {' '}
                  · HTTP <span className="font-mono">{senderPaused.httpStatus}</span>
                </>
              ) : null}
            </>
          ) : (
            'The error code could not be read.'
          )}
        </div>
      ) : null}
      {kind === 'auto_pause' && senderPaused.reason ? (
        <div className="text-xs">{senderPaused.reason}</div>
      ) : null}
      {senderPaused.pausedAt !== null ? (
        <div className="text-xs text-muted">
          Since{' '}
          <time dateTime={new Date(senderPaused.pausedAt).toISOString()}>
            {formatDateTime(senderPaused.pausedAt)}
          </time>
        </div>
      ) : null}
      {canResume && isAdmin ? (
        <ResumeSenderDialog
          senderId={senderPaused.senderId as string}
          kind={kind === 'auto_pause' ? 'auto_pause' : 'ses_halt'}
          killSwitchEngaged={killSwitchEngaged}
        />
      ) : null}
      {canResume && !isAdmin ? (
        <div className="text-xs text-muted">Only an administrator can resume the sender.</div>
      ) : null}
    </Card>
  );
}
